import React, { useEffect, useState } from 'react';
import { ShoppingCart, MessageCircle, CalendarCheck, FileText, Tag, ShieldCheck, BadgeCheck, Truck, PhoneCall } from 'lucide-react';
import { useCart } from '../context/CartContext';

const API = process.env.REACT_APP_BACKEND_URL;
const WA = '917011370322';

const wa = (text) => `https://wa.me/${WA}?text=${encodeURIComponent(text)}`;

const formatPrice = (price) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(price);

export const ProductCTAButtons = ({ slug, name, enquiryOnly = false }) => {
  const { addToCart } = useCart();
  const [product, setProduct] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/products/${slug}`)
      .then((r) => (r.ok ? r.json() : null))
      .then(setProduct)
      .catch(() => {});
  }, [slug]);

  const buyNow = () => {
    if (product) addToCart(product);
  };

  return (
    <section className="py-8 px-6 lg:px-12 bg-white border-y border-black/5" data-testid={`product-cta-${slug}`}>
      <div className="max-w-7xl mx-auto flex flex-col lg:flex-row lg:items-center gap-5">
        <div className="lg:mr-auto">
          <p className="font-heading text-lg font-semibold text-gray-900">{name}</p>
          {product && (
            <p className="flex items-baseline gap-2">
              {product.onSale && (
                <span className="text-sm text-gray-400 line-through">{formatPrice(product.originalPrice)}</span>
              )}
              <span className="text-2xl font-bold text-gray-900">{formatPrice(product.salePrice)}</span>
              <span className="text-xs text-emerald-600 font-medium">incl. GST · Free installation</span>
            </p>
          )}
        </div>
        <div className="flex flex-wrap gap-3">
          {enquiryOnly ? (
            <a
              href="tel:+917011370322"
              data-testid={`cta-expert-${slug}`}
              className="inline-flex items-center gap-2 rounded-full bg-gray-900 text-white px-6 py-3 text-sm font-semibold hover:bg-gray-700 transition-colors duration-300"
            >
              <PhoneCall size={16} /> Talk to an Expert
            </a>
          ) : (
            <button
              onClick={buyNow}
              data-testid={`cta-buy-now-${slug}`}
              className="inline-flex items-center gap-2 rounded-full bg-gray-900 text-white px-6 py-3 text-sm font-semibold hover:bg-gray-700 transition-colors duration-300"
            >
              <ShoppingCart size={16} /> Buy Now
            </button>
          )}
          <a
            href={wa(`Hello Harpalyk, I would like a quote for the ${name}.`)}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`cta-quote-${slug}`}
            className="inline-flex items-center gap-2 rounded-full bg-emerald-600 text-white px-6 py-3 text-sm font-semibold hover:bg-emerald-500 transition-colors duration-300"
          >
            <Tag size={16} /> Get Quote
          </a>
          <a
            href={wa(`Hello Harpalyk, I would like to know more about the ${name}.`)}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`cta-whatsapp-${slug}`}
            className="inline-flex items-center gap-2 rounded-full bg-[#25D366] text-white px-6 py-3 text-sm font-semibold hover:brightness-110 transition-[filter] duration-300"
          >
            <MessageCircle size={16} /> Chat on WhatsApp
          </a>
          <a
            href="/#contact"
            data-testid={`cta-demo-${slug}`}
            className="inline-flex items-center gap-2 rounded-full border border-gray-300 text-gray-900 px-6 py-3 text-sm font-semibold hover:border-gray-900 transition-colors duration-300"
          >
            <CalendarCheck size={16} /> Book Live Demo
          </a>
          <a
            href={wa(`Hello Harpalyk, please share the brochure for the ${name}.`)}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`cta-brochure-${slug}`}
            className="inline-flex items-center gap-2 rounded-full border border-gray-300 text-gray-900 px-6 py-3 text-sm font-semibold hover:border-gray-900 transition-colors duration-300"
          >
            <FileText size={16} /> Request Brochure
          </a>
        </div>
      </div>
      <div className="max-w-7xl mx-auto mt-5 pt-4 border-t border-black/5 flex flex-wrap items-center gap-x-8 gap-y-2 text-xs text-gray-500" data-testid={`trust-badges-${slug}`}>
        <span className="inline-flex items-center gap-1.5"><ShieldCheck size={14} className="text-emerald-600" /> Secure Razorpay Payment</span>
        <span className="inline-flex items-center gap-1.5"><BadgeCheck size={14} className="text-emerald-600" /> 2-Year Warranty</span>
        <span className="inline-flex items-center gap-1.5"><Truck size={14} className="text-emerald-600" /> Free Delivery &amp; Installation</span>
        <span className="inline-flex items-center gap-1.5"><PhoneCall size={14} className="text-emerald-600" /> Pan-India Service Support</span>
      </div>
    </section>
  );
};

export default ProductCTAButtons;
